/* eslint-disable no-underscore-dangle */
import React from 'react';
import styled from 'styled-components';
import { cloneDeep } from 'lodash';
import {
  convertTo24HrTimeNum,
  timeNumToFormattedStr,
  timeStrToNumber,
} from 'utils';
import { Modal, TimePicker } from 'components';
import {
  DAYS,
  TIMES,
  TimesStatus,
} from '@bb/domain/dist/types/AppointmentConfig';
import {
  AccessHoursType,
  IBoxAccessHoursType,
  IBoxDayLabelType,
  InvoyAccessHoursType,
  InvoyDayLabelType,
} from '@bb/domain/dist/types/SupraLockbox';
import moment from 'moment';

type IBoxAccessHoursValuesType = IBoxAccessHoursType<Date> & {
  applyAll: { startTime: Date; endTime: Date };
};

type InvoyAccessHoursValuesType = InvoyAccessHoursType<Date> & {
  applyAll: { startTime: Date; endTime: Date };
};

type AccessHoursValuesType =
  | IBoxAccessHoursValuesType
  | InvoyAccessHoursValuesType;

const DEFAULT_START_TIME = `2024-01-01T00:00`;
const DEFAULT_END_TIME = `2024-01-01T00:00`;

const defaultAccessHoursValues: AccessHoursValuesType = {
  applyAll: {
    startTime: new Date(DEFAULT_START_TIME),
    endTime: new Date(DEFAULT_END_TIME),
  },
  monday2Friday: {
    startTime: new Date(DEFAULT_START_TIME),
    endTime: new Date(DEFAULT_END_TIME),
  },
  monday: {
    startTime: new Date(DEFAULT_START_TIME),
    endTime: new Date(DEFAULT_END_TIME),
  },
  tuesday: {
    startTime: new Date(DEFAULT_START_TIME),
    endTime: new Date(DEFAULT_END_TIME),
  },
  wednesday: {
    startTime: new Date(DEFAULT_START_TIME),
    endTime: new Date(DEFAULT_END_TIME),
  },
  thursday: {
    startTime: new Date(DEFAULT_START_TIME),
    endTime: new Date(DEFAULT_END_TIME),
  },
  friday: {
    startTime: new Date(DEFAULT_START_TIME),
    endTime: new Date(DEFAULT_END_TIME),
  },
  saturday: {
    startTime: new Date(DEFAULT_START_TIME),
    endTime: new Date(DEFAULT_END_TIME),
  },
  sunday: {
    startTime: new Date(DEFAULT_START_TIME),
    endTime: new Date(DEFAULT_END_TIME),
  },
};

const IBOX_LABELS: Record<IBoxDayLabelType, string> = {
  monday2Friday: 'Mon - Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

const INVOY_LABELS: Record<InvoyDayLabelType, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

const datePickerFormatter = 'h:mm a';
const datePickerMinuteStep = 15;

interface LockboxHoursModalProps {
  lockboxType: 'Invoy' | 'iBox';
  supraLockboxAccessHours: AccessHoursType<Date>;
  timeslots: string[];
  visible: boolean;
  closeModal: () => void;
  selectTimes: (timeslots: [DAYS, TIMES][], toValue: TimesStatus) => void;
  updateLockboxHours: (accessHours: AccessHoursType<Date>) => void;
}

const LockboxHoursModal = (props: LockboxHoursModalProps) => {
  const {
    lockboxType,
    supraLockboxAccessHours,
    timeslots,
    visible,
    closeModal,
    selectTimes,
    updateLockboxHours,
  } = props;

  const allTimesMatch = Object.values(supraLockboxAccessHours).every(
    (day, idx, arr) => {
      if (idx === 0) return true;

      const prevDay = arr[idx - 1];

      return (
        moment(day.startTime).isSame(prevDay.startTime) &&
        moment(day.endTime).isSame(prevDay.endTime)
      );
    },
  );

  const [timeRangeError, setTimeRangeError] = React.useState(false);
  const [applyAll, setApplyAll] = React.useState(allTimesMatch);
  const [applyAllSnapshot, setApplyAllSnapshot] = React.useState(allTimesMatch);
  const [values, setValues] = React.useState(defaultAccessHoursValues);
  const [valuesSnapshot, setValuesSnapshot] = React.useState(
    defaultAccessHoursValues,
  );

  const modalLabels = React.useMemo(
    () => (lockboxType === 'iBox' ? IBOX_LABELS : INVOY_LABELS),
    [lockboxType],
  );

  const handleApplyAll = (e: any) => {
    setApplyAll(e.target.checked);
    if (e.target.checked && values) {
      const updatedValues = {} as AccessHoursType<Date>;
      Object.keys(modalLabels).forEach(day => {
        updatedValues[day as keyof AccessHoursType<Date>] = {
          startTime: values.applyAll.startTime,
          endTime: values.applyAll.endTime,
        };
      });
      setValues({
        ...values,
        ...updatedValues,
      });
    }
  };

  const validate = (start: Date, end: Date) => {
    if (
      start >= end &&
      start.getTime() !== new Date(DEFAULT_START_TIME).getTime() &&
      end.getTime() !== new Date(DEFAULT_END_TIME).getTime()
    )
      return 'error';
    return '';
  };

  const validateAll = (_values: AccessHoursValuesType) => {
    const containsError = Object.values(_values).some(
      ({ startTime, endTime }) => Boolean(validate(startTime, endTime)),
    );
    setTimeRangeError(containsError);
  };

  const updateHours = (
    day: keyof AccessHoursValuesType,
    field: 'startTime' | 'endTime',
    time: Date,
  ) => {
    const updatedValues = {
      ...values,
      [day]: {
        startTime: field === 'startTime' ? time : values[day].startTime,
        endTime: field === 'endTime' ? time : values[day].endTime,
      },
      ...(day === 'applyAll'
        ? {
            ...Object.fromEntries(
              Object.keys(modalLabels).map(
                (label: keyof AccessHoursValuesType) => [
                  label,
                  {
                    startTime:
                      field === 'startTime' ? time : values[label].startTime,
                    endTime: field === 'endTime' ? time : values[label].endTime,
                  },
                ],
              ),
            ),
          }
        : {}),
    };
    setValues(updatedValues);
    validateAll(updatedValues);
  };

  const resetValuesToSnapshot = () => {
    setValues({ ...values, ...valuesSnapshot });
    setApplyAll(applyAllSnapshot);
  };

  /**
   * @param day DKCoreBTLELabelType | DKCoreInvoyLabelType
   * @param time time number i.e 1000
   * @returns Array of [DAY, TIME] i.e. [[MON, "10:00"]]
   */
  const buildTimeslots = (day: string, time: number) => {
    const formattedDay =
      INVOY_LABELS[day as keyof typeof modalLabels]?.toUpperCase();
    const formattedTimeStr = timeNumToFormattedStr(time);
    return day !== 'monday2Friday'
      ? [[formattedDay, formattedTimeStr]]
      : [
          ['MON', formattedTimeStr],
          ['TUE', formattedTimeStr],
          ['WED', formattedTimeStr],
          ['THU', formattedTimeStr],
          ['FRI', formattedTimeStr],
        ];
  };

  const updateShowingRestrictions = (
    newHours: typeof values,
    previousHours: typeof values,
    lockboxTypeChange?: boolean,
  ) => {
    const _previousHours: any = cloneDeep(previousHours);
    const _newHours: any = cloneDeep(newHours);
    let _modalLabels = modalLabels;

    // Adjust for lockboxType change so showing restrictions update accordingly
    if (lockboxTypeChange && lockboxType !== 'iBox') {
      _previousHours.monday = { ..._previousHours.monday2Friday };
      _previousHours.tuesday = { ..._previousHours.monday2Friday };
      _previousHours.wednesday = { ..._previousHours.monday2Friday };
      _previousHours.thursday = { ..._previousHours.monday2Friday };
      _previousHours.friday = { ..._previousHours.monday2Friday };
    }
    if (lockboxTypeChange && lockboxType === 'iBox') {
      _modalLabels = INVOY_LABELS;
      _newHours.monday = { ..._newHours.monday2Friday };
      _newHours.tuesday = { ..._newHours.monday2Friday };
      _newHours.wednesday = { ..._newHours.monday2Friday };
      _newHours.thursday = { ..._newHours.monday2Friday };
      _newHours.friday = { ..._newHours.monday2Friday };
    }

    const _timeslots = timeslots.map(time => timeStrToNumber(time));

    const restrictedTimeslots: any[] = [];
    const availableTimeslots: any[] = [];

    Object.keys(_modalLabels).forEach(
      (day: IBoxDayLabelType | InvoyDayLabelType) => {
        const firstTimeslot = 700;
        const lastTimeslot = 2400;

        let startTime = convertTo24HrTimeNum(_newHours[day].startTime);
        let endTime = convertTo24HrTimeNum(_newHours[day].endTime);

        startTime =
          startTime < firstTimeslot
            ? firstTimeslot
            : startTime % 100 === 15
              ? startTime + 15
              : startTime % 100 === 45
                ? startTime + 55
                : startTime;

        endTime =
          endTime === 0
            ? lastTimeslot
            : endTime < firstTimeslot
              ? firstTimeslot
              : endTime % 100 === 15 || endTime % 100 === 45
                ? endTime - 15
                : endTime;

        let prevStartTime = convertTo24HrTimeNum(_previousHours[day].startTime);
        let prevEndTime = convertTo24HrTimeNum(_previousHours[day].endTime);

        prevStartTime =
          prevStartTime < firstTimeslot
            ? firstTimeslot
            : prevStartTime % 100 === 15
              ? prevStartTime + 15
              : prevStartTime % 100 === 45
                ? prevStartTime + 55
                : prevStartTime;

        prevEndTime =
          prevEndTime === 0
            ? lastTimeslot
            : prevEndTime < firstTimeslot
              ? firstTimeslot
              : prevEndTime % 100 === 15 || prevEndTime % 100 === 45
                ? prevEndTime - 15
                : prevEndTime;

        _timeslots.forEach(time => {
          if (time < startTime || time >= endTime) {
            restrictedTimeslots.push(...buildTimeslots(day, time));
          }

          if (startTime < prevStartTime) {
            while (startTime <= prevStartTime) {
              availableTimeslots.push(...buildTimeslots(day, prevStartTime));
              prevStartTime =
                prevStartTime % 100 === 30
                  ? (prevStartTime -= 30)
                  : (prevStartTime -= 70);
            }
          }

          if (endTime > prevEndTime) {
            while (endTime > prevEndTime) {
              availableTimeslots.push(...buildTimeslots(day, prevEndTime));
              prevEndTime =
                prevEndTime % 100 === 30
                  ? (prevEndTime += 70)
                  : (prevEndTime += 30);
            }
          }
        });
        selectTimes(restrictedTimeslots, 'Restricted');
        selectTimes(availableTimeslots, 'Available');
      },
    );
  };

  const confirmLockboxHours = () => {
    const daysToUpdate = {} as AccessHoursType<Date>;
    Object.keys(modalLabels).forEach((day: keyof AccessHoursType<Date>) => {
      daysToUpdate[day] = values[day];
    });
    updateLockboxHours(daysToUpdate);
    updateShowingRestrictions(values, valuesSnapshot);
    setValuesSnapshot(values);
    setApplyAllSnapshot(applyAll);
    closeModal();
  };

  React.useEffect(() => {
    const hours = {
      ...defaultAccessHoursValues,
      ...valuesSnapshot,
      ...supraLockboxAccessHours,
      ...(applyAll && {
        applyAll: {
          ...(supraLockboxAccessHours.sunday
            ? supraLockboxAccessHours.sunday
            : defaultAccessHoursValues.applyAll),
        },
      }),
    };
    setValues(hours);
    updateShowingRestrictions(hours, valuesSnapshot, true);
    setValuesSnapshot(hours);
  }, [lockboxType, supraLockboxAccessHours]);

  return (
    <Modal
      open={visible}
      title="Lockbox Access Hours"
      onCancel={() => {
        resetValuesToSnapshot();
        closeModal();
      }}
      okText="Confirm"
      okButtonProps={{ disabled: timeRangeError }}
      onOk={() => {
        confirmLockboxHours();
      }}
    >
      <StyledModalBody>
        <StyledInstructions>
          Select the hours for the lockbox to be accessible.
        </StyledInstructions>
        <StyledInput
          type="checkbox"
          checked={applyAll}
          onChange={e => {
            handleApplyAll(e);
          }}
        />
        <StyledApplyAllLabel>Apply All</StyledApplyAllLabel>
        <StyledTimeRangeContainer>
          <TimePicker
            format={datePickerFormatter}
            use12Hours
            showNow={false}
            minuteStep={datePickerMinuteStep}
            value={values.applyAll.startTime}
            onChange={time => updateHours('applyAll', 'startTime', time!)}
            disabled={!applyAll}
            status={validate(
              values.applyAll.startTime,
              values.applyAll.endTime,
            )}
          />{' '}
          -
          <TimePicker
            format={datePickerFormatter}
            use12Hours
            showNow={false}
            minuteStep={datePickerMinuteStep}
            value={values.applyAll.endTime}
            onChange={time => updateHours('applyAll', 'endTime', time!)}
            disabled={!applyAll}
            status={validate(
              values.applyAll.startTime,
              values.applyAll.endTime,
            )}
          />
        </StyledTimeRangeContainer>
        <StyledDaysArea>
          {Object.entries(modalLabels).map(
            ([key, label]: [keyof AccessHoursValuesType, string], i) => (
              <StyledDayContainer key={i}>
                <StyledDayLabel>{label}</StyledDayLabel>
                <StyledTimeRangeContainer>
                  <TimePicker
                    format={datePickerFormatter}
                    use12Hours
                    showNow={false}
                    minuteStep={datePickerMinuteStep}
                    value={values[key].startTime}
                    onChange={time => updateHours(key, 'startTime', time!)}
                    disabled={applyAll}
                    status={validate(
                      values[key].startTime,
                      values[key].endTime,
                    )}
                  />
                  -
                  <TimePicker
                    format={datePickerFormatter}
                    use12Hours
                    showNow={false}
                    minuteStep={datePickerMinuteStep}
                    value={values[key].endTime}
                    onChange={time => updateHours(key, 'endTime', time!)}
                    disabled={applyAll}
                    status={validate(
                      values[key].startTime,
                      values[key].endTime,
                    )}
                  />
                </StyledTimeRangeContainer>
              </StyledDayContainer>
            ),
          )}
        </StyledDaysArea>
      </StyledModalBody>
    </Modal>
  );
};

export default LockboxHoursModal;

const StyledModalBody = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr 4fr;
  grid-template-rows: auto;
  grid-template-areas:
    'instructions instructions instructions'
    'checkbox applyAllLabel applyAllPicker'
    '. dayPicker dayPicker';
  align-items: center;
`;

const StyledInstructions = styled.div`
  grid-area: instructions;
  padding-bottom: 20px;
`;

const StyledInput = styled.input`
  grid-area: checkbox;
  justify-self: start;
  margin-right: 5px;
`;

const StyledApplyAllLabel = styled.div`
  grid-area: applyAllLabel;
  font-weight: 500;
`;

const StyledDaysArea = styled.div`
  grid-area: dayPicker;
`;

const StyledDayContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
`;

const StyledDayLabel = styled.div`
  width: 20%;
  font-weight: 500;
`;

const StyledTimeRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;
